import copy from 'copy-to-clipboard';

import {Button} from '#component/base';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useToastQueue} from '#hook';

import {ShareTriggerProps, ShareTriggerRenderProp} from './ShareTrigger.types';

const DefaultTrigger: ShareTriggerRenderProp = ({onClick}) => (
  <Button onClick={onClick}>Share</Button>
);

/**
 * ShareTrigger accepts the input required to generate a link for the `/share`
 * route, as well as `children` as a render prop which should be able to accept
 * an `onClick` prop.
 *
 * This `onClick` prop will handle invoking either the browser's native `share`
 * functionality, or will copy the share URL to the user's clipboard.
 */
export const ShareTrigger = ({
  actionName = 'Request share story URL',
  children = DefaultTrigger,
  formattedDeltaAge,
  percentile,
  isYounger = false,
}: ShareTriggerProps) => {
  const params = {
    formattedDeltaAge: formattedDeltaAge || {months: 0, years: 0},
    percentile,
    isYounger,
  };
  const {addToast} = useToastQueue();
  const {track} = useAnalytics();
  const url = `${process.env.NEXT_PUBLIC_FE_URL}/share?age=${btoa(JSON.stringify(params))}`;

  const handleShare = async () => {
    track<UserActionEvent>({
      actionName,
      evtName: EventType.UserAction,
    });

    if (!navigator.canShare) {
      copy(`Check out my TallyAge™: ${url}`);
      addToast({children: 'Link copied'});
    } else {
      try {
        await navigator.share({
          text: 'Check out my TallyAge™:',
          title: 'Tally Health™',
          url,
        });
      } catch (e) {
        console.warn('Unable to invoke `navigator.share`', e);
      }
    }
  };

  return <>{children?.({onClick: handleShare})}</>;
};
